import {
  Button,
  Dialog,
  DialogTitle,
  Paper,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useState } from "react";
import { useSelector } from "react-redux";
import { selectUserWebsitePermissions } from "../../../../../../features/userSlice";

interface AllLinkedLandingPagesProps {
  open: boolean;
  handleClose: () => void;
  LinkedTableHeadings: { [key: string]: string };
  LinkedRows: any[];
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: "asc" | "desc",
  orderBy: Key
) {
  return order === "desc"
    ? (a: { [key in Key]: any }, b: { [key in Key]: any }) =>
        descendingComparator(a, b, orderBy)
    : (a: { [key in Key]: any }, b: { [key in Key]: any }) =>
        -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const AllLinkedLandingPagesModal = ({
  open,
  handleClose,
  LinkedTableHeadings,
  LinkedRows,
}: AllLinkedLandingPagesProps) => {
  const headCells = Object.entries(LinkedTableHeadings);

  // Default sort: clicks descending
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("clicks");

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedSite = searchParams.get("selectedSite") as any;
  const allWebsites = useSelector(selectUserWebsitePermissions);
  const matchedWebsite = allWebsites?.find(
    (site: any) => Object.keys(site)[0] === selectedSite
  );
  const matchedUrl = matchedWebsite
    ? matchedWebsite[selectedSite]?.site_url?.replace(/\/$/, "")
    : null;

  // Check if there's data to display
  const hasData = LinkedRows && LinkedRows.length > 0;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      PaperProps={{ sx: { width: "100%", maxWidth: "1000px" } }}
    >
      <Paper sx={{ padding: "16px 24px 20px 24px" }}>
        <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
          Landing Pages linked to this Keyword
        </DialogTitle>

        {hasData ? (
          <TableContainer
            className="KeywordsTableWrap"
            component={Paper}
            sx={{ boxShadow: "none", maxHeight: 640 }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map(([key, value], index) => (
                    <TableCell key={index} align="left">
                      <TableSortLabel
                        active={orderBy === key}
                        direction={orderBy === key ? order : "asc"}
                        onClick={() => handleRequestSort(key)}
                      >
                        {value && typeof value === "string" ? value : ""}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {stableSort(
                  LinkedRows,
                  getComparator(order, orderBy as keyof any)
                ).map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell
                        key={cellIndex}
                        align="left"
                        sx={{ py: "3px" }}
                      >
                        {key === "page_url" ? (
                          <RouterLink
                            to={row[key] ? `${matchedUrl}${row[key]}` : ""}
                            style={{
                              textDecoration: "underline",
                              color: "#9C27B0",
                            }}
                            target="_blank"
                          >
                            {row[key] ? `${matchedUrl}${row[key]}` : ""}
                          </RouterLink>
                        ) : key === "cost" ? (
                          row[key] != null ? (
                            `${Number(row[key]).toLocaleString("en-US")}$`
                          ) : (
                            ""
                          )
                        ) : key in row ? (
                          row[key] ?? ""
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="body1"
            align="center"
            sx={{ py: 4, color: "text.secondary" }}
          >
            No data available
          </Typography>
        )}

        <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Close
          </Button>
        </Stack>
      </Paper>
    </Dialog>
  );
};

export default AllLinkedLandingPagesModal;
